import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Award, Download, Share2, CheckCircle, Calendar, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Certificates = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCertificates();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchCertificates = async () => {
    try {
      const res = await axios.get(`${API}/certificates`, { headers: getAuthHeaders() });
      setCertificates(res.data.certificates);
    } catch (error) {
      console.error('Error fetching certificates:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const shareCertificate = (cert) => {
    const url = `${window.location.origin}/verify/${cert.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Certificate link copied to clipboard!');
  };

  const downloadCertificate = (cert) => {
    toast.info('Certificate download coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      <nav className="glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <h1 className="text-2xl font-bold">My Certificates</h1>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-purple-600/10 rounded-full mb-4">
            <Award className="h-12 w-12 text-purple-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Your Achievements</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Blockchain-verified credentials to showcase your DeFi expertise
          </p>
        </div>

        {certificates.length === 0 ? (
          <Card className="bg-white/5 border-white/10 max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <Award className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Certificates Yet</h3>
              <p className="text-gray-400 mb-4">Complete courses to earn certificates</p>
              <Button onClick={() => navigate('/academy')}>Start Learning</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {certificates.map((cert) => (
              <Card key={cert.id} className="bg-gradient-to-br from-purple-600/10 to-indigo-600/10 border-purple-500/30">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <Badge className="bg-purple-500/20 text-purple-300 mb-3">VERIFIED</Badge>
                      <CardTitle className="text-2xl mb-2">{cert.course_name}</CardTitle>
                      <CardDescription>Awarded to {cert.user_name}</CardDescription>
                    </div>
                    <Shield className="h-8 w-8 text-purple-400" />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Calendar className="h-4 w-4" />
                    <span>Issued: {new Date(cert.issued_date).toLocaleDateString()}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    <span className="text-green-400">Blockchain Verified</span>
                  </div>

                  <div className="p-3 bg-secondary rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Certificate ID</p>
                    <p className="text-xs font-mono">{cert.id}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => downloadCertificate(cert)}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={() => shareCertificate(cert)}
                    >
                      <Share2 className="h-4 w-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-12 bg-white/5 border-white/10">
          <CardContent className="py-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <Shield className="h-12 w-12 text-blue-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-2">Blockchain Verified</h3>
                <p className="text-sm text-gray-400">Immutable proof on Sepolia testnet</p>
              </div>
              <div>
                <Share2 className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-2">Easy Sharing</h3>
                <p className="text-sm text-gray-400">Add to LinkedIn or resume</p>
              </div>
              <div>
                <Award className="h-12 w-12 text-purple-400 mx-auto mb-3" />
                <h3 className="text-lg font-bold mb-2">Career Boost</h3>
                <p className="text-sm text-gray-400">Stand out to crypto employers</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Certificates;